import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, SearchIcon, CheckIcon } from 'lucide-react';
const HeroSection = () => {
  // Animation states for bullet points and underlines
  const [animationStates, setAnimationStates] = useState({
    bulletPoint1: false,
    bulletPoint2: false,
    bulletPoint3: false,
    bulletPoint4: false,
    underlineReframe: false,
    underlineInterpret: false,
    underlineCreate: false,
    underlineUnpack: false
  });
  // Trigger animations with staggered timing
  useEffect(() => {
    const animationTimers = [
    // Bullet point animations
    setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint1: true
    })), 1300), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint2: true
    })), 1600), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint3: true
    })), 1900), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      bulletPoint4: true
    })), 2200),
    // Sequential underline animations (after bullet points appear)
    setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineReframe: true
    })), 2500), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineInterpret: true
    })), 3100), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineCreate: true
    })), 3700), setTimeout(() => setAnimationStates(prev => ({
      ...prev,
      underlineUnpack: true
    })), 4300)];
    // Cleanup timers on unmount
    return () => animationTimers.forEach(timer => clearTimeout(timer));
  }, []);
  return <section className="min-h-screen overflow-hidden bg-white" data-id="element-241">
      {/* Content */}
      <div className="relative z-20 flex items-center min-h-screen" data-id="element-242">
        <div className="max-w-7xl mx-auto w-full px-4 py-12 md:py-16" data-id="element-243">
          <div className="flex flex-col md:flex-row items-start justify-between gap-12" data-id="element-244">
            {/* Left Column */}
            <div className="md:w-1/2 md:pt-0 md:-mt-20" data-id="element-245">
              {/* Logo */}
              <div className="mb-5" data-id="element-246">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mJeiiWYx1MRS4z54BWWeae/Final_Emigo_Logo_%28Transparent%29-Photoroom.png" alt="Emigo Logo" className="h-[300px] md:h-[375px] w-auto" data-id="element-247" />
              </div>
              {/* Content container - moved up more */}
              <div className="flex flex-col -mt-8" data-id="element-248">
                {/* Text content group - now static except for bullet points */}
                <div className="space-y-12 md:space-y-16 mb-8" data-id="element-249">
                  {/* Main Heading - static now */}
                  <h1 className="text-3xl md:text-6xl font-semibold leading-tight text-gray-900" style={{
                  fontFamily: "'DM Serif Display', serif"
                }} data-id="element-250">
                    <span className="block" data-id="element-251">Say what you mean,</span>
                    <span className="block whitespace-nowrap" data-id="element-252">
                      not what you'll regret.
                    </span>
                  </h1>
                  {/* Sub Heading - static now */}
                  <div data-id="element-253">
                    <h2 className="text-xl md:text-3xl font-semibold text-gray-600 mb-4" style={{
                    fontFamily: "'DM Serif Display', serif"
                  }} data-id="element-254">
                      Emigo's got your back when you need to:
                    </h2>
                    {/* Branded checkmark list with individual animations - increased spacing between items */}
                    <div className="space-y-6 mb-8" data-id="element-255">
                      {/* Bullet point 1 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-256">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-257">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-258" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-259">
                          <span className={`relative italic ${animationStates.underlineReframe ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-260">
                            Reframe
                          </span>{' '}
                          a heated or unclear text
                        </span>
                      </div>
                      {/* Bullet point 2 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-261">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-262">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-263" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-264">
                          <span className={`relative italic ${animationStates.underlineInterpret ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-265">
                            Interpret
                          </span>{' '}
                          a tricky message and how to best reply
                        </span>
                      </div>
                      {/* Bullet point 3 - with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-266">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-267">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-268" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-269">
                          <span className={`relative italic ${animationStates.underlineCreate ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-270">
                            Create
                          </span>{' '}
                          the right words when you're stuck
                        </span>
                      </div>
                      {/* Bullet point 4 - Unpack with italics and underline animation */}
                      <div className={`flex items-start transform transition-all duration-500 ease-out ${animationStates.bulletPoint4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-271">
                        <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E87B5C] flex items-center justify-center mr-3" data-id="element-272">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-273" />
                        </div>
                        <span className="text-lg md:text-2xl text-gray-600" data-id="element-274">
                          <span className={`relative italic ${animationStates.underlineUnpack ? 'after:content-[""] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:bg-[#E87B5C] after:w-full after:transition-all after:duration-500' : ''}`} data-id="element-275">
                            Unpack
                          </span>{' '}
                          the deeper meaning behind a conversation
                        </span>
                      </div>
                    </div>
                    {/* Closing line - updated to be slightly more grey and "real time" in italics */}
                    <p className="text-base md:text-xl text-gray-500" data-id="element-276">
                      All in <em data-id="element-277">real time</em>, without switching apps: whether
                      you're trying to land a date, keep your relationship
                      strong, stay calm with family, or sound professional at
                      work.
                    </p>
                  </div>
                </div>
                {/* Buttons - static now and reduced margin-top by 30% */}
                <div className="flex flex-col w-full mt-11 items-start" data-id="element-278">
                  {/* Changed from flex-col to flex-row and increased width */}
                  <div className="flex flex-col sm:flex-row gap-4 w-full max-w-[580px]" data-id="element-279">
                    <a href="#download" className="group px-6 py-3 bg-[#E87B5C] text-white text-lg font-medium rounded-full hover:bg-[#D16A4A] transition-all flex items-center justify-center min-w-[160px] shadow-sm hover:shadow-md" data-id="element-280">
                      Download Now
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-281" />
                    </a>
                    <a href="#how-it-works" className="px-6 py-3 bg-white text-gray-800 text-lg font-medium rounded-full border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all flex items-center justify-center min-w-[160px]" data-id="element-282">
                      Why Emigo
                      <SearchIcon className="ml-2 h-5 w-5" data-id="element-283" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Right Column - iPhone - Updated for better centering on mobile */}
            <div className="w-full md:w-1/2 flex justify-center items-center mt-8 md:mt-0 md:items-start md:pt-24" data-id="element-284">
              <div className="relative mx-auto w-[240px] md:w-[290px] rounded-[40px] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden aspect-[9/19.5] transform rotate-3 transition-all duration-300 hover:rotate-0 hover:animate-wiggle" data-id="element-285">
                {/* Sound waves animation (visible on hover) */}
                <div className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity duration-300 z-30" data-id="element-286">
                  <div className="sound-wave sound-wave-1" data-id="element-287"></div>
                  <div className="sound-wave sound-wave-2" data-id="element-288"></div>
                  <div className="sound-wave sound-wave-3" data-id="element-289"></div>
                </div>
                <div className="absolute -right-12 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity duration-300 z-30" data-id="element-290">
                  <div className="sound-wave sound-wave-1" data-id="element-291"></div>
                  <div className="sound-wave sound-wave-2" data-id="element-292"></div>
                  <div className="sound-wave sound-wave-3" data-id="element-293"></div>
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/2CkSSZqEUGGZEBFfyLzKnF/0.png" alt="Emigo Reframe feature preview" className="w-full h-full object-cover object-top" data-id="element-294" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default HeroSection;